<script setup lang="ts">
import BaseInput from "@/core/components/base/BaseInput.vue";
import BaseSelect from "@/core/components/base/BaseSelect.vue";
import {ref} from "vue";
import Icon from "@/core/components/icon/Icon.vue";
import {Popover, PopoverContent, PopoverTrigger} from "@/core/components/ui/popover";
import {RangeCalendar} from "@/core/components/ui/range-calendar";
import {DateRange} from "reka-ui";
import {getLocalTimeZone, today} from "@internationalized/date";
import BaseButton from "@/core/components/base/BaseButton.vue";
import BaseRadio from "@/core/components/base/BaseRadio.vue";
import {loyaltyCardApi,} from "@/container";
import {
    SearchBarAutoCompleteItem
} from "@/auto-complete-searchbar/interfaces/ISearchBarAutoComplete";
import {useRoute, useRouter} from "vue-router";
import {useSystemStore} from "@/stores/system.store";
import SearchBarAutoCompleteService from "@/auto-complete-searchbar/services/SearchBarAutoCompleteService";
import {ProductSearchBarOutput} from "@/auto-complete-searchbar/interfaces";
import {pluralize} from "@/core/filters/Pluralize";
import {CreateLoyaltyCardAnimationParams} from "@/core/http/resources/loyalty-card/LoyaltyCardResource";
import LoyaltyCardProductsDialog from "@/loyalty-card/components/LoyaltyCardProductsDialog.vue";

const name = ref<string>('');
const description = ref<string>('');
const coefficient = ref<string>("2");
const loading = ref<boolean>(false);
const selectedProducts = ref<SearchBarAutoCompleteItem[]>([]);
const {environment} = useSystemStore();
const date = ref<DateRange>({
    start: today(getLocalTimeZone()),
    end: today(getLocalTimeZone()),
});
const openCalendar = ref<boolean>(false);
const allProducts = ref<boolean>();
const route = useRoute();
const router = useRouter();

/**
 * Select an item.
 * @param data  The selected item.
 */
const select = (data: SearchBarAutoCompleteItem | null) => {
    if (data) {
        const index = selectedProducts.value.findIndex(product => product.id === data.id && product.type === data.type);

        if (index !== -1) {
            selectedProducts.value.splice(index, 1);
        } else {
            selectedProducts.value.push(data);
        }
    }
};

/**
 * Create a loyalty card animation.
 *
 * @return {Promise<void> | Promise<LoyaltyCardAnimation>}
 */
const create = (): Promise<void> => {
    if (environment?.entityId && route.query?.loyaltyCardId && name && description && coefficient) {
        const params: CreateLoyaltyCardAnimationParams = {
            name: name.value,
            description_patient: description.value,
            coefficient: Number(coefficient.value),
            start_date: date.value?.start ? date.value.start.toDate(getLocalTimeZone()) : today(getLocalTimeZone()).toDate(getLocalTimeZone()),
            end_date: date.value?.end ? date.value.end.toDate(getLocalTimeZone()) : today(getLocalTimeZone()).toDate(getLocalTimeZone()),
            is_for_all_products: !!allProducts.value
        };

        loading.value = true;

        if (!allProducts.value && !!selectedProducts.value?.length) {
            return SearchBarAutoCompleteService.transform(ProductSearchBarOutput.PRODUCTS, selectedProducts.value)
                .then(response => {
                    params.products = response;
                    return loyaltyCardApi().createAnimation(environment.entityId, Number(route.query.loyaltyCardId), params);
                })
                .then(() => router.back())
                .finally(() => loading.value = false);
        }

        return loyaltyCardApi().createAnimation(environment.entityId, Number(route.query.loyaltyCardId), params)
            .then(() => router.back())
            .finally(() => loading.value = false);
    }

    return Promise.resolve();
};
</script>

<template>
  <div class="p-4">
    <h2>Créer une animation</h2>
    <p>Ces informations seront affichées sur l'application Apodis côté patient.</p>
    <div class="flex flex-col py-4 gap-4">
      <BaseInput :value="name" label="Nom" type="text" @input="value => name = value" />
      <BaseInput :value="description" label="Description" type="text" @input="value => description = value" />
      <div>
        <label
          class="block text-sm font-medium text-gray-700"
        >
          Coefficient
        </label>
        <BaseSelect
          v-model:model-value="coefficient"
          :options="[{label: 'x1.5', value: '1.5'}, {'label': 'x2', value: '2'}, {'label': 'x3', value: '3'}]"
        />
      </div>
      <label class="block text-sm font-medium text-gray-700">Période de l'animation</label>
      <Popover v-model:open="openCalendar" @update:open="(event) => openCalendar = event">
        <PopoverTrigger as-child>
          <button class="hide-options flex gap-3 items-center border bg-white p-2 -mt-3 rounded-lg">
            <Icon name="mdi-calendar" />
            <p v-if="date.start && date.end">
              {{ $filters.date(date.start.toDate(getLocalTimeZone()), 'short') }} au
              {{ $filters.date(date.end.toDate(getLocalTimeZone()), 'short') }}
            </p>
          </button>
        </PopoverTrigger>
        <PopoverContent class="w-auto p-0">
          <RangeCalendar v-model="date as DateRange" initial-focus />
        </PopoverContent>
      </Popover>
      <div>
        <label class="block text-sm font-medium text-gray-700">Produits éligibles</label>
        <BaseRadio
          v-model="allProducts"
          :value="true"
          label="Tous les produits de la carte de fidélité"
        />
        <BaseRadio
          v-model="allProducts"
          :value="false"
          label="Sélectionner les produits à lier à l'animation"
        />
        <div v-if="selectedProducts.length" class="my-2">
          Vous avez sélectionné {{ selectedProducts.length }} {{ pluralize('produit', selectedProducts.length) }}
          <div class="flex flex-row flex-wrap gap-2 mt-2">
            <div
              v-for="selectedProduct in selectedProducts"
              :key="selectedProduct.id"
              class="cursor-pointer hover:opacity-50"
              @click="select(selectedProduct)"
            >
              <p class="bg-primary p-2 rounded-lg">
                {{ selectedProduct.name }}
              </p>
            </div>
          </div>
        </div>
        <div v-if="allProducts === false" class="flex flex-row justify-center pt-2">
          <LoyaltyCardProductsDialog :selected-products="selectedProducts" @on-select="select" />
        </div>
      </div>
      <div class="flex flex-row justify-center">
        <BaseButton
          primary
          :loading="loading"
          :disabled="!name || !description || allProducts === undefined"
          @click="create"
        >
          Valider
        </BaseButton>
      </div>
    </div>
  </div>
</template>
